import NP from 'number-precision'
import { formatSpecs } from './format-specs'
import { routerKey, type RouteLocationNormalizedLoaded } from 'vue-router'
import { getTrafficSourceParts, type ISourceMeta } from '@/utils/traffic-source'
export { setSEOMeta, setStructuredData } from './seo'
export type { ISourceMeta }

/** ----  库存 START ---- */
export type PathMap = { [key: string]: ProductV2.ISku[] }
export interface IUpdateStatusOption {
  // skuId: number,
  skuKey: string,
  skus: ProductV3.Sku[]
  specs: ProductV3.Specs[]
}

/**
 * 更新 specs 状态
 * @param {*} currentSpec 当前sku key  xx:xxxx|xx:xxxx
 * @param {*} skus skus列表
 * @param {*} specs 规格列表
 */
export function updateStatus(
  opt: IUpdateStatusOption,
  skuActivateInfo?: ProductV3.SelectedSkuActivity,
) {
  const { /** skuId */ skuKey, skus, specs } = opt

  // 只有一个规格值不需要进行处理
  if(specs.length <= 1) { return }

  // 废弃：目前库存再SKU里面返回，切一定会选中某个完整的SKU
  // !pathMap[String(listingIdOrSkuId)] && (pathMap[String(listingIdOrSkuId)] = getPathMap(skus))

  formatSpecs(skuKey, specs, skus, skuActivateInfo)
}

/**
* 初始化规格，禁用无库存的规格选项
* @param {*} specs
* @param {*} skus
*/
export function disableOutOfStockSpecs (
 specs: ProductV3.Specs[], 
 skus: ProductV3.Sku[]
) {
  const removedSkus = skus.filter((c: ProductV3.Sku) => c.status > 0 )
  // 可用的SKU
  const soldOutSKus = skus.filter((c: ProductV3.Sku) => {
    const { inventory, status } = c
    const {gsAvailable, dsAvailable, fbkAvailable} = inventory

    return Math.max(gsAvailable, dsAvailable, fbkAvailable) > 0 && status > 0
  })

  specs.forEach((c, index) => {
    const { values, pid } = c
    values.forEach((v: ProductV3.Values, valIndex: number) => {
      const { pvid } = v
      const curSpecKey = `${pid}:${pvid}`
      
      const hasStock = soldOutSKus.find((sku: ProductV3.Sku) => {
        const specKeys = sku.key.split(';')
        
        return  specKeys.some(_k => _k === curSpecKey)
      })
      v.disabled = !hasStock
      
      const hasRemoved = removedSkus.find((sku: ProductV3.Sku) => {
        const specKeys = sku.key.split(';')
        
        return specKeys.some(_k => _k === curSpecKey)
      })

      v.disabledRemoved = !hasRemoved
    })
  })
}
/** ----  库存 END ---- */

/**
 * 通过 SKU ID 获取价格
 * @param skuId 
 * @param source 0 普通商品 1 秒杀商品
 * @returns 
 */
export function initSkuPrice(sku?: ProductV3.Sku): ProductV2.ISkuPrice {
  if(!sku) {
    return { salePrice: 0, listPrice: 0, isSale: true, discount: 0, source: null }
  }

  const { listPrice, salePrice, isSale } = sku

  let _price = {
    listPrice,
    salePrice,
    isSale,
    discount: Number(Math.floor(NP.times(1 - NP.divide(salePrice, listPrice), 100))),
    source: sku
  }

  return _price
}

/**
 * 初始化SKU ID
 * @param skuIds 
 * @param skuList 
 * @returns 
 */
export const initSkuId = (skuId: number, skuList: ProductV3.Sku[]): number | undefined => {
  const _skus = skuList.filter(sku => {
    const { fbkAvailable, dsAvailable, gsAvailable } = sku.inventory

    return Math.max(fbkAvailable, dsAvailable, gsAvailable) > 0
  })

  const _curSku = _skus.find(sku => sku.skuId === Number(skuId))
  const _curSourceSku = skuList.find(sku => sku.skuId === Number(skuId))
  const _sid = _curSku?.skuId || _skus[0]?.skuId || _curSourceSku?.skuId

  return _sid
}

/**
 * 通过 SKU 价格信息初始化活动信息
 * @param skuPriceSource SKU价格原信息
 * @returns ProductV2.IActivateInfo
 */
export function initActivateInfoBySkuPrice (skuPriceSource: any): ProductV2.IActivateInfo | undefined {
  const {
    activityId, activityType, activityPrice, queryTime,
    startTime, endTime, bigDiscountSale, flashSale, listPrice
  } = skuPriceSource
  
  if(!activityType) {
    return
  }

  return {
    activityId, activityType, activityPrice, queryTime,
    startTime, endTime, bigDiscountSale, flashSale,
    discount: Number((NP.minus(1, NP.divide(activityPrice, listPrice)) * 100).toFixed(2))
  }
}

/**
 * 获取最大折扣ID
 * @param buyerPrice 
 * @param skuCount 
 * @param promotions 
 * @returns 
 */
export const getPromotionId = (buyerPrice: number, skuCount: number, promotions: ProductV2.IBigPromotion[]): number => {
  const priceTotal = NP.times(skuCount, buyerPrice)

  let discounts = 0
  let id = -1
  promotions.filter(c => priceTotal > c.rule.priceBreak).forEach(c => {
    if(c.rule.discount > discounts) {
      discounts = c.rule.discount
      id = c.id
    }
  })
  
  return id
}

// 存储商品的品类信息
export interface IListingCategoryItem {
  listingId: number,
  categoryList: number[],
  timestamp: number
}
const setListingCategoryList = (listingCategoryItem: IListingCategoryItem, key: string) => {
  // 先判断是否已经存储过
  const listingIdCategoryList = localStorage.getItem(key)
  if (listingIdCategoryList) {
    const listingIdCategoryListObj = JSON.parse(listingIdCategoryList)
    // 判断listingIdCategoryList是否包含了listingCategoryItem， 如包含则更新时间戳， 未包含就添加到数组存储
    const isExist = listingIdCategoryListObj.some(item => String(item.listingId) === String(listingCategoryItem.listingId))
    if (isExist) {
      listingIdCategoryListObj.forEach(item => {
        if (String(item.listingId) === String(listingCategoryItem.listingId)) {
          item.timestamp = Date.now()
        }
      })
    } else {
      listingIdCategoryListObj.push(listingCategoryItem)
    }
    localStorage.setItem(key, JSON.stringify(listingIdCategoryListObj))
    return
  } else {
    // 不存在listingIdCategoryList时创建
    localStorage.setItem(key, JSON.stringify([listingCategoryItem]))
  }
}
const getListingCategoryList = (key: string) => {
  const listingIdCategoryList = localStorage.getItem(key)
  if (listingIdCategoryList) {
    return JSON.parse(listingIdCategoryList)
  }
  return []
}
// 商品浏览
export const setReviewListingIdCategoryList = (listingCategoryItem: IListingCategoryItem) => {
  setListingCategoryList(listingCategoryItem, 'reviewListingIdCategoryList')
}
export const getReviewListingIdCategoryList = () => {
  return getListingCategoryList('reviewListingIdCategoryList')
}
// 商品支付
export const setPayListingIdCategoryList = (listingCategoryItem: IListingCategoryItem) => {
  setListingCategoryList(listingCategoryItem, 'payListingIdCategoryList')
}
export const getPayListingIdCategoryList = () => {
  return getListingCategoryList('payListingIdCategoryList')
}
/**
 * 获取品类数据，有一定的规则
 * 第1优先级 - 当天支付过的最近商品, 当取的商品<3个时(去重后), 则按照：
 * 第2优先级 - 当天浏览过的最近商品, 当取的商品<3个时(去重后), 则按照：
 * 第3优先级 - 过去7天支付过的最近商品, 当取的商品<3个时(去重后), 则按照
 * 第4优先级 - 过去7天浏览过的最近商品, 当取的商品<3个时(去重后), 则按照
 * 第5优先级 - 过去30天支付的最近商品, 当取的商品<3个时(去重后), 则按照
 * 第6优先级 - 过去30天浏览的最近商品,  当取的商品<3个时(去重后), 则按照
 * 兜底策略 - 过去90天浏览的随机商品
 */
export const getCategoryListByPriority = (): number[] => {
  const now = Date.now()
  const oneDayMs = 24 * 60 * 60 * 1000
  
  // 获取支付和浏览记录
  const payList = getPayListingIdCategoryList() as IListingCategoryItem[]
  const reviewList = getReviewListingIdCategoryList() as IListingCategoryItem[]
  
  // 按时间戳降序排序(最新的在前)
  const sortByTimestamp = (list: IListingCategoryItem[]) => 
    list.sort((a, b) => b.timestamp - a.timestamp)
  
  sortByTimestamp(payList)
  sortByTimestamp(reviewList)
  
  // 过滤指定时间范围内的记录
  const filterByDays = (list: IListingCategoryItem[], days: number) => 
    list.filter(item => now - item.timestamp <= days * oneDayMs)
  
  // 从列表中选择指定数量的商品(去重 listingId)
  const selectProducts = (list: IListingCategoryItem[], usedListingIds: Set<number>, maxCount: number) => {
    const result: IListingCategoryItem[] = []
    for (const item of list) {
      if (result.length >= maxCount) break
      
      // 确保不重复选择同一个商品
      if (!usedListingIds.has(item.listingId)) {
        result.push(item)
        usedListingIds.add(item.listingId)
      }
    }
    return result
  }
  
  const selectedProducts: IListingCategoryItem[] = []
  const usedListingIds = new Set<number>()
  const maxProducts = 3
  // 第1优先级: 当天支付过的最近商品
  if (selectedProducts.length < maxProducts) {
    const todayPay = filterByDays(payList, 1)
    selectedProducts.push(...selectProducts(todayPay, usedListingIds, maxProducts - selectedProducts.length))
  }
  // 第2优先级: 当天浏览过的最近商品
  if (selectedProducts.length < maxProducts) {
    const todayReview = filterByDays(reviewList, 1)
    selectedProducts.push(...selectProducts(todayReview, usedListingIds, maxProducts - selectedProducts.length))
  }
  
  // 第3优先级: 过去7天支付过的最近商品
  if (selectedProducts.length < maxProducts) {
    const week7Pay = filterByDays(payList, 7)
    selectedProducts.push(...selectProducts(week7Pay, usedListingIds, maxProducts - selectedProducts.length))
  }
  
  // 第4优先级: 过去7天浏览过的最近商品
  if (selectedProducts.length < maxProducts) {
    const week7Review = filterByDays(reviewList, 7)
    selectedProducts.push(...selectProducts(week7Review, usedListingIds, maxProducts - selectedProducts.length))
  }
  
  // 第5优先级: 过去30天支付的最近商品
  if (selectedProducts.length < maxProducts) {
    const month30Pay = filterByDays(payList, 30)
    selectedProducts.push(...selectProducts(month30Pay, usedListingIds, maxProducts - selectedProducts.length))
  }
  
  // 第6优先级: 过去30天浏览的最近商品
  if (selectedProducts.length < maxProducts) {
    const month30Review = filterByDays(reviewList, 30)
    selectedProducts.push(...selectProducts(month30Review, usedListingIds, maxProducts - selectedProducts.length))
  }
  
  // 兜底策略: 过去90天浏览的随机商品
  if (selectedProducts.length < maxProducts) {
    const day90Review = filterByDays(reviewList, 90)
    // 随机打乱顺序
    const shuffled = day90Review.sort(() => Math.random() - 0.5)
    selectedProducts.push(...selectProducts(shuffled, usedListingIds, maxProducts - selectedProducts.length))
  }
  
  // 从选中的商品中提取所有品类ID并去重
  const categoryIds = new Set<number>()
  selectedProducts.forEach(product => {
    product.categoryList.forEach(categoryId => {
      categoryIds.add(categoryId)
    })
  })
  
  return Array.from(categoryIds)
}


export interface ITrackConfig{
  skuId: number,
  listingId: number,
  source: string,
  trafficSourceMeta?: ISourceMeta,
  productTitle: string,
  categoryId: number,
  isAd?: boolean,
  isNewTag?: boolean,
  isChoiceTag?: boolean,
  requestId?: string,
  algorithmModelName?: string,
  store?: Store.IStore,
  skuPrice?: ProductV2.ISkuPrice,
  isAdd?: boolean
  productInventoryType?: ILogisticsType
}

/** 聊天发送商品时，将 source 最后一段（internalLatestTrafficSourcePage）设为 liveChat */
export const toLiveChatSource = (source: string, meta?: ISourceMeta): string => {
  const parts = [...getTrafficSourceParts(source, meta)]
  while (parts.length < 5) parts.push('')
  parts[4] = 'liveChat'
  return parts.slice(0, 5).join('-')
}

export interface ITrafficSourceTracking {
  internalLatestTrafficSourceModule?: string
  internalLatestTrafficSourceType?: string
  insideLatestTrafficSourceValue?: string
  internalFirstTrafficSourcePage?: string
  internalLatestTrafficSourcePage?: string
  algorithmModelName?: string
  isAd?: boolean
  isNewTag?: boolean
  isChoiceTag?: boolean
  requestId?: string
}

export interface ITrafficSourceQuery {
  source?: string
  trafficSourceMeta?: ISourceMeta
  isAd?: boolean | string
  isNewTag?: boolean
  isChoiceTag?: boolean
  requestId?: string
  algorithmModelName?: string
}

/** 由 url-cache / query 参数构建 goods-detail 埋点字段 JSON 字符串 */
export const buildTrafficSourceTracking = (params: ITrafficSourceQuery): string => {
  const [
    internalLatestTrafficSourceModule = '',
    internalLatestTrafficSourceType = '',
    insideLatestTrafficSourceValue = '',
    internalFirstTrafficSourcePage = '',
    internalLatestTrafficSourcePage = ''
  ] = getTrafficSourceParts(params.source, params.trafficSourceMeta)

  const tracking: ITrafficSourceTracking = {
    internalLatestTrafficSourceModule,
    internalLatestTrafficSourceType,
    insideLatestTrafficSourceValue,
    internalFirstTrafficSourcePage,
    internalLatestTrafficSourcePage,
    algorithmModelName: params.algorithmModelName || '',
    isAd: params.isAd === 'ad' || params.isAd === true,
    isNewTag: !!params.isNewTag,
    isChoiceTag: !!params.isChoiceTag,
    requestId: params.requestId || ''
  }

  return JSON.stringify(tracking)
}

export const trackFn = (
  config: ITrackConfig,
  eventName?: any,
) => {
  const { $SENSORS_EVENT, $serverTrack, $getExternalLastTrafficSource } = useNuxtApp()
  eventName = eventName || $SENSORS_EVENT.viewProductDetail
  
  const { skuId, listingId, source, trafficSourceMeta, categoryId, store, skuPrice, productInventoryType, productTitle, requestId } = config
  if(!skuId || !skuPrice || !store) { return }
  const algorithmModelName = config.algorithmModelName || ''
  const [
    internalLatestTrafficSourceModule = '',
    internalLatestTrafficSourceType = '',
    insideLatestTrafficSourceValue = '',
    internalFirstTrafficSourcePage = '',
    internalLatestTrafficSourcePage = ''
  ] = getTrafficSourceParts(source, trafficSourceMeta)

  const skuTrackData ={
    categoryId: categoryId,
    listingId: listingId,
    productPrice: skuPrice.salePrice,
    skuId: skuId,
    storeId: store.id,
    storeName: store.name,
    productInventoryType: productInventoryType || '',
    productTitle: productTitle || '',
    algorithmModelName
  }
  
  const trackData = {
    ...skuTrackData,
    internalLatestTrafficSourceModule,
    internalLatestTrafficSourceType,
    insideLatestTrafficSourceValue,
    internalFirstTrafficSourcePage,
    internalLatestTrafficSourcePage,
    ...$getExternalLastTrafficSource()
  }

  $serverTrack(eventName, trackData, {
    isAd: !!config.isAd,
    isNewTag: !!config.isNewTag,
    isChoiceTag: !!config.isChoiceTag,
    requestId: config.requestId || ''
  })
}

export const tiktokTrack = (
  eventName: string,
  qty: number,
  sku?: ProductV3.Sku,
  detail?: ProductV3.ListingDetailV3,
) => {
  if(!window.ttq || !sku || !detail) {
    return
  }
  const { title, categoriesPath } = detail
  const { salePrice, skuId } = sku

  const _td = {
    "contents": [
      {
        "content_id": String(skuId), // string. ID of the product. Example: "1077218"
        "content_name": title, // string. The name of the page or product. Example: "shirt".
        "price": salePrice,
        "quantity": qty,
        "content_category": String(categoriesPath[categoriesPath.length - 1]?.id || '')
      }
    ],
    "content_type": 'product',
    "value": salePrice, // number. Value of the order or items sold. Example: 100.
    "currency": "KES" // string. The 4217 currency code. Example: "USD".
  }

  // tiktok 事件追踪
  window.ttq.track(eventName, _td)
}

export class LazyQueue {
  private static instance: LazyQueue | null = null
  private delayQueue: any[] = []
  private timeout: number = 3000

  constructor(timeout: number = 3000) {    
    if(!LazyQueue.instance) {
      this.delayQueue = []
      this.timeout = timeout
      LazyQueue.instance = this

      this.runTask()
    }

    return LazyQueue.instance
  }

  addTask(task: any) {
    this.delayQueue.unshift(task)
  }

  runTask() {
  // 延时任务队列（浏览器空闲时执行，超过 3s 强制插入当前事件循环）
    const idleCb = (idle: IdleDeadline) => {
      if(idle.timeRemaining() > 5 || idle.didTimeout) {
        if(this.delayQueue.length > 0) {
          const fn = this.delayQueue.pop()
          fn && fn()
        } 
      }

      requestIdleCallback(idleCb, { timeout: this.timeout } )
    }

    requestIdleCallback(idleCb, { timeout: this.timeout } )
  }

  destroy() {
    this.delayQueue = []

    LazyQueue.instance = null
  }
}

export const getShowArrivalReminder = (
  currentSelectedSku?: ProductV3.Sku,
  skuActivateInfo?: ProductV3.SelectedSkuActivity,
  store?: Store.IStore
) => {
  // 秒杀商品不处理
  // if(!skuActivateInfo || (skuActivateInfo?.activityType === 1 && skuActivateInfo?.flashSale?.status === 1)) {
  if(skuActivateInfo?.activityType === 1 && skuActivateInfo?.flashSale?.status === 1) {
    return false
  }

  if(!currentSelectedSku) {
    return true
  }

  // 店铺歇业
  if(store?.status === 2) {
    return false
  }
  // 下架商品
  if(currentSelectedSku.status !== 1) {
    return false
  }
  const { fbkAvailable, dsAvailable, gsAvailable } = currentSelectedSku!.inventory

  // 任意类型有库存，不显示
  if(Math.max(fbkAvailable, dsAvailable, gsAvailable) > 0) {
    return false
  }

  return true
}

interface IQuery{
  type?: 'sku',
  source?: string,
  skuId?: string,
  addressId?: string,
  from?: 'sku' | 'flash-sale'
  isAd?: 'ad'
}
interface IParams { id?: string }
interface IHash {}
export const formatQueryParams = (route: RouteLocationNormalizedLoaded) => {
  const query = route.query as IQuery
  
  const params = route.params as IParams
  if(!params?.id) {
    console.error('params id is empty!')
    throw createError({
      statusCode: 404,
      message: 'The product does not exist.',
      fatal: true
    });
  }

  const listingId = params.id.split('-')[0]
  const cookieParams = useCookie(`_${listingId}_`).value

  useHead({
    script: [
      {
        innerHTML: JSON.stringify(cookieParams),
        type: 'application/json',
        id: 'listing-json'
      }
    ]
  });

  useCookie(`_${listingId}_`).value = undefined

  console.log('route: ', cookieParams)
}